<template>
  <div
    class="tw-relative tw-ml-4 tw-w-full tw-flex-1"
    :data-test="`contract-version-${step.versionNumber}`"
  >
    <div
      class="tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-right-[calc(100%+52px)] tw-flex tw-w-28 tw-flex-col tw-items-end tw-gap-1"
      data-test="contract-gutter"
    >
      <span
        v-if="effectiveDateLabel"
        class="tw-text-gray-800 tw-text-md"
        data-test="contract-effective-date"
      >
        {{ effectiveDateLabel }}
      </span>
      <WdsTooltip left global :disabled="!step.scheduleLapsed" :text="lapsedScheduleHint">
        <WdsTag
          sm
          :primary="step.bucket === 'scheduled'"
          :success="step.bucket === 'active'"
          data-test="contract-status"
          :data-bucket="step.bucket"
          :data-lapsed="step.scheduleLapsed ? 'true' : undefined"
        >
          <span class="tw-flex tw-items-center tw-gap-1">
            {{ groupLabel }}
            <WdsIcon v-if="step.scheduleLapsed" name="information" :size="14" />
          </span>
        </WdsTag>
      </WdsTooltip>
    </div>
    <BaseListRow
      clickable
      class="tw-flex tw-items-center tw-justify-between tw-gap-4"
      :data-test="`contract-open-${step.versionNumber}`"
      @click="openContract"
    >
      <div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
        <div class="tw-flex tw-items-center tw-gap-2">
          <span class="tw-font-semibold tw-text-lg" data-test="contract-version-label">
            {{ $t('Version') }} {{ step.versionNumber }}
          </span>
          <WdsTag v-if="step.isOriginal" sm subtle data-test="contract-original-tag">
            {{ $t('Original') }}
          </WdsTag>
          <WdsTag
            v-if="isAwaitingSignature"
            sm
            warning
            data-test="contract-signature"
            data-state="awaiting"
          >
            {{ $t('Awaiting signature') }}
          </WdsTag>
        </div>
        <template v-if="detailRows.length > 0">
          <hr class="tw-my-1 tw-w-full tw-border-0 tw-border-t tw-border-gray-200" />
          <div
            class="tw-flex tw-gap-y-1"
            :class="showsSummary ? 'tw-flex-wrap tw-gap-x-6' : 'tw-flex-col'"
            data-test="contract-details"
          >
            <div
              v-for="row in detailRows"
              :key="row.field"
              class="tw-flex tw-items-baseline tw-gap-2"
              :class="{ 'tw-flex-wrap': !showsSummary }"
              data-test="contract-detail"
              :data-field="row.field"
            >
              <span class="tw-text-gray-700">{{ row.label }}</span>
              <template v-if="row.from">
                <span class="tw-text-gray-700 tw-line-through">{{ row.from }}</span>
                <span class="tw-text-gray-700">&rarr;</span>
              </template>
              <span class="tw-font-semibold">{{ row.to }}</span>
            </div>
          </div>
        </template>
      </div>

      <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-600">
        <WdsIcon name="chevron-right" :size="20" />
      </div>
    </BaseListRow>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import moment from 'moment'
import { WdsTag, WdsIcon, WdsTooltip } from '@worksome/wds-components'
import BaseListRow from '@/components/BaseListRow.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { HireActiveStatus } from '@/gql/graphql'
import type {
  ContractBucket,
  ContractChangeField,
  ContractHistoryEntry,
  ContractHistoryStep,
} from '@/components/Hire/contractHistory'
import { formatRate } from '@/composables/useContractDiff'
import { useContractDrawer } from '@/components/Hire/composables/useContractDrawer'

const props = defineProps<{
  step: ContractHistoryStep
  canEdit: boolean
  hireActiveStatus?: HireActiveStatus | null
}>()

const { openContractDrawer } = useContractDrawer()

const openContract = () => {
  if (props.step.isLatest && props.canEdit) {
    openContractDrawer('show')
    return
  }
  openContractDrawer('view', props.step.contract.id)
}

const GROUP_LABELS: Record<ContractBucket, string> = {
  scheduled: $t('Scheduled'),
  draft: $t('Draft'),
  active: $t('Active'),
  archived: $t('Archived'),
}

const groupLabel = computed(() => GROUP_LABELS[props.step.bucket])

const lapsedScheduleHint = $t(
  "The worker hasn't signed this change yet, so it takes effect on the day they do.",
)

const fmt = (value?: string | null) => (value ? moment(value).format('MMM D, YYYY') : '')

const signedDate = computed(
  () => props.step.contract.termsAcceptedAt ?? props.step.contract.workerAcceptedAt ?? null,
)

const effectiveDateLabel = computed(() => fmt(props.step.effectiveFrom))

const isAwaitingSignature = computed(() => {
  if (signedDate.value) {
    return false
  }
  const isPending = props.step.bucket === 'draft' || props.step.bucket === 'scheduled'
  const hireIsOver =
    props.hireActiveStatus === HireActiveStatus.Terminated ||
    props.hireActiveStatus === HireActiveStatus.Cancelled

  return isPending && !hireIsOver
})

const FIELD_LABELS: Record<ContractChangeField, string> = {
  rate: $t('Rate'),
  startDate: $t('Start date'),
  endDate: $t('End date'),
}

const SUMMARY_FIELDS: ContractChangeField[] = ['rate', 'startDate', 'endDate']

const showsSummary = computed(() => props.step.previous === null)

const valueOf = (contract: ContractHistoryEntry, field: ContractChangeField) =>
  (field === 'rate' ? formatRate(contract) : fmt(contract[field])) || '—'

const detailRows = computed(() => {
  const { previous, changes, contract } = props.step

  if (previous === null) {
    return SUMMARY_FIELDS.map((field) => ({
      field,
      label: FIELD_LABELS[field],
      from: '',
      to: valueOf(contract, field),
    }))
  }

  return changes.map((field) => ({
    field,
    label: FIELD_LABELS[field],
    from: valueOf(previous, field),
    to: valueOf(contract, field),
  }))
})
</script>

<style lang="scss" scoped></style>
